import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Send } from "lucide-react";
import { trpc } from "@/trpc/trpc";
import { toast } from "sonner";
import { PromotionDialog } from "@/components/promotion-dialog";
import { Pagination } from "@/components/pagination";

export const Route = createFileRoute("/_authenticated/notifications")({
  component: NotificationsPage,
});

function formatDate(d: Date | string) {
  return new Date(d).toLocaleDateString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

type Promotion = {
  id: string;
  title: string;
  body: string;
  type: "push" | "email";
  sentAt: Date | string | null;
  sentCount: number;
  openCount: number;
  scheduledFor: Date | string | null;
  treatmentId: string | null;
  collectionId: string | null;
};

function NotificationsPage() {
  const [page, setPage] = useState(1);
  const { data, isLoading } = trpc.promotion.list.useQuery({ page, limit: 20 });
  const utils = trpc.useUtils();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Promotion | null>(null);

  const deletePromotion = trpc.promotion.delete.useMutation({
    onSuccess: () => {
      utils.promotion.list.invalidate();
      toast.success("Notyfikacja usunięta");
    },
    onError: (err) => toast.error(err.message),
  });

  const sendPromotion = trpc.promotion.send.useMutation({
    onSuccess: () => {
      utils.promotion.list.invalidate();
      toast.success("Notyfikacja wysłana!");
    },
    onError: (err) => toast.error(err.message),
  });

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Ładowanie...</p>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Notyfikacje</h1>
          <p className="text-sm text-muted-foreground">
            {data?.total ?? 0} notyfikacji (max 1 push/tydzień)
          </p>
        </div>
        <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }}>
          <Plus className="mr-1 size-4" />
          Nowa notyfikacja
        </Button>
      </div>

      {data?.items.length === 0 && (
        <Card className="p-8 text-center">
          <p className="text-sm text-muted-foreground">Brak notyfikacji — stwórz pierwszą kampanię</p>
        </Card>
      )}

      <div className="grid gap-4">
        {data?.items.map((promo) => (
          <Card key={promo.id} className="p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold">{promo.title}</h3>
                  <Badge variant={promo.sentAt ? "secondary" : "outline"}>
                    {promo.sentAt ? "Wysłana" : "Zaplanowana"}
                  </Badge>
                  <Badge variant="outline">{promo.type}</Badge>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{promo.body}</p>
                <p className="mt-2 text-xs text-muted-foreground">
                  {promo.sentAt
                    ? `Wysłano: ${formatDate(promo.sentAt)}`
                    : promo.scheduledFor
                      ? `Zaplanowana: ${formatDate(promo.scheduledFor)}`
                      : "Nie wysłana"}
                </p>
                {promo.sentAt && promo.sentCount > 0 && (
                  <div className="mt-2 flex items-center gap-3 text-xs">
                    <span className="text-muted-foreground">
                      Wysłano: <span className="font-medium text-foreground">{promo.sentCount}</span>
                    </span>
                    <span className="text-muted-foreground">
                      Otwarto: <span className="font-medium text-foreground">{promo.openCount}</span>
                    </span>
                    <span className="text-muted-foreground">
                      Open rate:{" "}
                      <span className="font-medium text-foreground">
                        {Math.round((promo.openCount / promo.sentCount) * 100)}%
                      </span>
                    </span>
                  </div>
                )}
              </div>
              {!promo.sentAt && (
                <div className="flex gap-1">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => {
                      if (confirm("Wysłać push do wszystkich użytkowników salonu?")) {
                        sendPromotion.mutate({ id: promo.id });
                      }
                    }}
                    disabled={sendPromotion.isPending}
                  >
                    <Send className="mr-1 size-4" />
                    Wyślij
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => { setEditing(promo); setDialogOpen(true); }}>
                    Edytuj
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => deletePromotion.mutate({ id: promo.id })}>
                    <Trash2 className="size-4 text-destructive" />
                  </Button>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>

      {(data?.totalPages ?? 1) > 1 && (
        <Pagination page={page} totalPages={data?.totalPages ?? 1} onPageChange={setPage} />
      )}

      <PromotionDialog open={dialogOpen} onOpenChange={setDialogOpen} promotion={editing} />
    </div>
  );
}
